import { capitalize, cn, formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetReconciliationReport } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { cardTemplates } from "./consts";

const useReconciliationReport = () => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const communityName = communityData?.name || communityData?.communityName || "";
  const [globalFilter, setGlobalFilter] = useState<string>("");
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: "",
        name: <span className="text-primary">Cost Analytics Reconciliation Report</span>,
      },
    ],
    [communityName]
  );
  
  const {data: reportData, isLoading: isLoadingReportData} = useGetReconciliationReport({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })

  // Inside your component:
  const summaryCards = useMemo(() => {  
    
    if (!reportData) return [];

    const structuralCards: any = cardTemplates.map((card) => {
      // Dynamic key selection from our accumulated totals dictionary object
      const amtValue = reportData[card?.amtKey] || 0;
      const descValue = reportData[card?.descKey as keyof typeof reportData] || 0;
  
      return {
        ...card,
        id: card.id,
        title: card.title,
        amt: formatAmount(String(amtValue)),
        desc: `${descValue} ${card.suffix}`,
        amtValue
      };
    });
  
    const difference = structuralCards[0]?.amtValue - structuralCards[1]?.amtValue;
    const isBalanced = difference === 0;
    const variance = reportData?.rows?.filter((data: any) => data?.status?.toLowerCase() == "variance");

    // 4. Return the combined dataset
    return [
      ...structuralCards,
      {
        id: "diff",
        title: "Difference",
        amt: formatAmount(String(difference)),
        desc: "GL - Cost Analytics",
        isBalanced
      },
      {
        id: "integrity",
        title: "Integrity",
        text: isBalanced ? "Balanced" : `x ${variance?.length} Variance(s)`,
        desc: isBalanced ? "No value lost or double-counted" : "Investigate flagged accounts",
        isBalanced
      },
    ];
  }, [reportData, cardTemplates]);

  const columns = [
    {
      accessorKey: "glAccountNumber",
      header: <div>GL Account</div>,
    },
    {
      accessorKey: "glBalance",
      meta: { align: "right" },
      header: <div>GL Balance</div>,
      accessorFn: (row: any) => formatAmount(row?.glBalance || "0")
    },
    {
      accessorKey: "costAnalyticsBalance",
      meta: { align: "right" },
      header: <div>Cost Analytics Balance</div>,
      accessorFn: (row: any) => formatAmount(row?.costAnalyticsBalance || "0")
    },
    {
      accessorKey: "difference",
      meta: { align: "right" },
      header: <div>Difference</div>,
      accessorFn: (row: any) => formatAmount(row?.difference || "0")
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const isMatched = row?.original?.status?.toLowerCase() == "matched";
        return <span className={cn(isMatched ? "text-green-600" : "text-red-600")}>
          {capitalize(row?.original?.status)}
        </span>
      }
    },
  ];

  return {
    breadcrumbData,
    isLoadingReportData,
    summaryCards,
    columns,
    globalFilter,
    setGlobalFilter,
    reportData,
    currentFinanceYear
  }
}

export default useReconciliationReport;
